package er.prateek.crossly.ui.navigation

import er.prateek.crossly.data.model.DifficultyTier
import kotlinx.serialization.Serializable

/**
 * Type-safe navigation routes (androidx.navigation kotlinx-serialization routes).
 * Add new screens here as new @Serializable entries — no string route constants.
 */
sealed interface Destination {
    @Serializable
    data object Home : Destination

    @Serializable
    data object Settings : Destination

    @Serializable
    data object PrivacyPolicy : Destination

    // startTier is the enum's name (String), not DifficultyTier directly --
    // androidx.navigation's type-safe routes only auto-derive a NavType for
    // primitives, so a custom enum needs an explicit typeMap or a primitive
    // stand-in; the string name is the simplest stand-in.
    @Serializable
    data class LevelSelect(val startTier: String = DifficultyTier.EASY.name) : Destination

    @Serializable
    data class Crossword(
        val puzzleId: String,
        val fromChallenge: Boolean = false,
        val challengerName: String = "",
        val challengerTimeMs: Long = 0,
    ) : Destination

    @Serializable
    data object Daily : Destination

    // --- Math modes (math crossword + rapid-fire riddles). startTier carries the enum name. ---
    @Serializable
    data class MathLevelSelect(val startTier: String = DifficultyTier.EASY.name) : Destination

    @Serializable
    data class MathCrossword(val levelId: String, val isDaily: Boolean = false) : Destination

    @Serializable
    data class RiddleLevelSelect(val startTier: String = DifficultyTier.EASY.name) : Destination

    @Serializable
    data class RiddleRun(
        val levelId: String,
        val isDaily: Boolean = false,
        /** Question seed; `0` means "roll a fresh one". Set when replaying a friend's challenge. */
        val seed: Long = 0,
        val fromChallenge: Boolean = false,
        val challengerName: String = "",
        val challengerTimeMs: Long = 0,
        val challengerScore: Int = 0,
    ) : Destination

    /** Shared end screen for the leveled math modes. [mode] is a [er.prateek.crossly.data.model.GameMode] name. */
    @Serializable
    data class ModeResults(
        val mode: String,
        val levelId: String,
        val stars: Int,
        val elapsedMs: Long = 0,
        /** Riddles only: the seed the set was played with, so it can be shared as a challenge. */
        val seed: Long = 0,
        val correct: Int = 0,
        val total: Int = 0,
        /** Riddles only: the correct answers that run needed to clear the set. */
        val passMark: Int = 0,
        val fromChallenge: Boolean = false,
        val challengerName: String = "",
        val challengerTimeMs: Long = 0,
        val challengerScore: Int = 0,
    ) : Destination

    /** Between-games stop in a Brainstorm run. [completedMode] is a [er.prateek.crossly.data.model.GameMode] name. */
    @Serializable
    data class BrainstormBreak(
        val completedMode: String,
        val stars: Int,
        val roundComplete: Boolean,
    ) : Destination

    @Serializable
    data object Achievements : Destination

    @Serializable
    data class Results(
        val puzzleId: String,
        val stars: Int,
        val isDaily: Boolean = false,
        val fromChallenge: Boolean = false,
        val challengerName: String = "",
        val elapsedMs: Long = 0,
        val challengerTimeMs: Long = 0,
    ) : Destination
}
